"use client";

import { useRouter, useSearchParams } from "next/navigation";
import { Button, Select, SelectItem, Tabs, Tab } from "@heroui/react";
import { X } from "lucide-react";
import {
  FilterStatistics,
  Country,
  City,
} from "@/app/(platform)/events/actions";

interface FilterData {
  countries: Country[];
  cities: City[];
  years: number[];
  statistics: FilterStatistics;
}

interface FilterPanelProps {
  filterData: FilterData;
  currentFilters: {
    search?: string;
    countrySlug?: string;
    countryName?: string;
    citySlug?: string;
    cityName?: string;
    month?: string;
    year?: string;
    type?: "upcoming" | "past" | "all";
  };
  onClose: () => void;
}

const months = [
  { value: "1", label: "January" },
  { value: "2", label: "February" },
  { value: "3", label: "March" },
  { value: "4", label: "April" },
  { value: "5", label: "May" },
  { value: "6", label: "June" },
  { value: "7", label: "July" },
  { value: "8", label: "August" },
  { value: "9", label: "September" },
  { value: "10", label: "October" },
  { value: "11", label: "November" },
  { value: "12", label: "December" },
];

export default function FilterPanel({
  filterData,
  currentFilters,
  onClose,
}: FilterPanelProps) {
  const router = useRouter();
  const searchParams = useSearchParams();

  // Helper function to build the new URL structure
  const buildEventUrl = (filters: typeof currentFilters) => {
    const currentPath =
      typeof window !== "undefined" ? window.location.pathname : "/events";

    // If we're on the main /events page, use query parameters for country/city
    if (currentPath === "/events") {
      const params = new URLSearchParams();

      // Add country and city as query parameters using names (not slugs)
      if (filters.countryName) params.set("country", filters.countryName);
      if (filters.cityName) params.set("city", filters.cityName);

      // Add other filters
      if (filters.year) params.set("year", filters.year);
      if (filters.type && filters.type !== "upcoming")
        params.set("type", filters.type);
      if (filters.month) params.set("month", filters.month);
      if (filters.search) params.set("search", filters.search);

      const queryString = params.toString();
      return queryString ? `/events?${queryString}` : "/events";
    }

    // For path-based URLs (country/city pages), use the slug-based structure
    let basePath = "/events";
    if (filters.countrySlug) {
      basePath += `/country/${filters.countrySlug}`;
      if (filters.citySlug) {
        basePath += `/city/${filters.citySlug}`;
      }
    }

    // Add other filters as query parameters
    const params = new URLSearchParams();
    if (filters.year) params.set("year", filters.year);
    if (filters.type && filters.type !== "upcoming")
      params.set("type", filters.type);
    if (filters.month) params.set("month", filters.month);
    if (filters.search) params.set("search", filters.search);

    const queryString = params.toString();
    return queryString ? `${basePath}?${queryString}` : basePath;
  };

  const updateFilter = (key: string, value: string | null) => {
    const newFilters = { ...currentFilters };

    if (key === "country") {
      if (value) {
        // Find the selected country object to get both name and slug
        const selectedCountry = filterData.countries.find(
          (c) => c.name === value
        );
        if (selectedCountry) {
          newFilters.countryName = selectedCountry.name;
          newFilters.countrySlug = selectedCountry.slug;

          // ALWAYS clear city when country changes (even if we had a city before)
          delete newFilters.cityName;
          delete newFilters.citySlug;
        }
      } else {
        // Clear country and city when country is cleared
        delete newFilters.countryName;
        delete newFilters.countrySlug;
        delete newFilters.cityName;
        delete newFilters.citySlug;
      }
    } else if (key === "city") {
      if (value) {
        // Find the selected city object to get both name and slug
        const selectedCity = filterData.cities.find((c) => c.name === value);
        if (selectedCity) {
          newFilters.cityName = selectedCity.name;
          newFilters.citySlug = selectedCity.slug;
        }
      } else {
        // Clear city
        delete newFilters.cityName;
        delete newFilters.citySlug;
      }
    } else {
      // Handle other filters (year, month, type, search)
      if (value) {
        newFilters[key as keyof typeof currentFilters] = value as any;
      } else {
        delete newFilters[key as keyof typeof currentFilters];
      }
    }

    const newUrl = buildEventUrl(newFilters);
    
    // Preserve the current filter panel state (on/off)
    const currentFiltersParam = searchParams.get("filters");
    let finalUrl = newUrl;
    
    if (currentFiltersParam === "on") {
      // Keep filters panel open if it was already open
      finalUrl = `${newUrl}${newUrl.includes("?") ? "&" : "?"}filters=on`;
    }
    // If filters param is not "on" or doesn't exist, don't add it (keep panel closed)
    
    // Use replace for filter changes to avoid cluttering browser history
    router.replace(finalUrl, { scroll: false });
  };

  const clearAllFilters = () => {
    router.push("/events", { scroll: false });
  };

  console.log("Fk filter data", filterData);

  const hasActiveFilters =
    currentFilters.search ||
    currentFilters.countryName ||
    currentFilters.cityName ||
    currentFilters.year ||
    currentFilters.month ||
    (currentFilters.type && currentFilters.type !== "upcoming");

  return (
    <div className="bg-surface/30 p-6 rounded-lg space-y-4">
      <div className="flex justify-between items-center">
        <h3 className="text-lg font-semibold">Filter Events</h3>
        {hasActiveFilters && (
          <Button
            variant="light"
            onPress={clearAllFilters}
            startContent={<X className="w-4 h-4" />}
            size="sm"
          >
            Clear All
          </Button>
        )}
      </div>

      {/* Event Type Tabs */}
      <div>
        <Tabs
          selectedKey={currentFilters.type || "upcoming"}
          onSelectionChange={(key) =>
            updateFilter("type", key === "upcoming" ? null : (key as string))
          }
          className="w-full"
        >
          <Tab
            key="upcoming"
            title={`Upcoming (${filterData.statistics.totals.upcoming})`}
          />
          <Tab key="all" title={`All (${filterData.statistics.totals.all})`} />
          <Tab
            key="past"
            title={`Past (${filterData.statistics.totals.past})`}
          />
        </Tabs>
      </div>

      {/* Location and Date Filters */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <Select
          placeholder="Select Country"
          selectedKeys={
            currentFilters.countryName ? [currentFilters.countryName] : []
          }
          onSelectionChange={(keys) => {
            const countryName = Array.from(keys)[0] as string;
            updateFilter("country", countryName || null);
          }}
          classNames={{
            trigger: "bg-surface/50",
          }}
        >
          {filterData.countries.map((country) => (
            <SelectItem key={country.name}>{country.name}</SelectItem>
          ))}
        </Select>

        <Select
          placeholder="Select City"
          selectedKeys={
            currentFilters.cityName ? [currentFilters.cityName] : []
          }
          onSelectionChange={(keys) => {
            const cityName = Array.from(keys)[0] as string;
            updateFilter("city", cityName || null);
          }}
          isDisabled={!currentFilters.countryName}
          classNames={{
            trigger: "bg-surface/50",
          }}
        >
          {filterData.cities.map((city) => (
            <SelectItem key={city.name}>{city.name}</SelectItem>
          ))}
        </Select>

        <Select
          placeholder="Select Year"
          selectedKeys={currentFilters.year ? [currentFilters.year] : []}
          onSelectionChange={(keys) =>
            updateFilter("year", (Array.from(keys)[0] as string) || null)
          }
          classNames={{
            trigger: "bg-surface/50",
          }}
        >
          {filterData.years.map((year) => (
            <SelectItem key={year.toString()}>{year.toString()}</SelectItem>
          ))}
        </Select>

        <Select
          placeholder="Select Month"
          selectedKeys={currentFilters.month ? [currentFilters.month] : []}
          onSelectionChange={(keys) =>
            updateFilter("month", (Array.from(keys)[0] as string) || null)
          }
          isDisabled={!currentFilters.year}
          classNames={{
            trigger: "bg-surface/50",
          }}
        >
          {months.map((month) => (
            <SelectItem key={month.value}>{month.label}</SelectItem>
          ))}
        </Select>
      </div>

      <div className="flex gap-2">
        <Button variant="flat" onPress={onClose}>
          Close
        </Button>
      </div>
    </div>
  );
}
